import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Crown, Calendar, Clock, Settings, Coins } from 'lucide-react'
import { useSubscriptionStore } from '../stores/subscriptionStore'
import { useRraasiCoinStore } from '../stores/rraasiCoinStore'
import SubscriptionPlans from './SubscriptionPlans'

const SubscriptionStatus: React.FC = () => {
  const { 
    currentSubscription, 
    isSubscriptionActive, 
    getActivePlan,
    getRemainingChatCalls,
    getRemainingCompatibilityCalls,
    usageStats
  } = useSubscriptionStore()
  const { balance } = useRraasiCoinStore()
  const [showPlans, setShowPlans] = useState(false)
  const [showDetails, setShowDetails] = useState(false)

  const activePlan = getActivePlan()

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  const getDaysRemaining = () => {
    if (!currentSubscription) return 0
    const now = new Date()
    const endDate = new Date(currentSubscription.endDate)
    const diffTime = endDate.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return Math.max(0, diffDays)
  }

  const getStatusColor = () => {
    const daysRemaining = getDaysRemaining()
    if (daysRemaining > 7) return 'text-green-400'
    if (daysRemaining > 3) return 'text-yellow-400'
    return 'text-red-400'
  }

  if (!isSubscriptionActive()) {
    return (
      <>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowPlans(true)}
          className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-xl font-semibold flex items-center gap-2 text-sm relative"
        >
          <Crown className="w-4 h-4" />
          Upgrade
          {/* Usage indicator */}
          <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {getRemainingChatCalls() + getRemainingCompatibilityCalls()}
          </div>
        </motion.button>

        {showPlans && (
          <SubscriptionPlans onClose={() => setShowPlans(false)} />
        )}
      </>
    )
  }

  return (
    <div className="relative">
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setShowDetails(!showDetails)}
        className="bg-gray-800 text-white px-4 py-2 rounded-xl font-semibold flex items-center gap-2 text-sm hover:bg-gray-700 transition-colors"
      >
        <Crown className="w-4 h-4 text-purple-400" />
        {activePlan?.name}
      </motion.button>

      {/* Subscription Details Dropdown */}
      {showDetails && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="absolute right-0 top-full mt-2 w-80 bg-gray-800 rounded-xl shadow-2xl border border-gray-700 z-50"
        >
          <div className="p-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-purple-400" />
                <span className="font-semibold text-white">{activePlan?.name}</span>
              </div>
              <button
                onClick={() => setShowDetails(false)}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>

            {/* Status */}
            <div className="mb-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-sm">Status</span>
                <span className="text-green-400 text-sm font-semibold">Active</span>
              </div>
            </div>

            {/* Dates */}
            <div className="space-y-2 mb-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-sm flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Started
                </span>
                <span className="text-white text-sm">
                  {currentSubscription ? formatDate(currentSubscription.startDate) : '-'}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-sm flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  Expires
                </span>
                <span className="text-white text-sm">
                  {currentSubscription ? formatDate(currentSubscription.endDate) : '-'}
                </span>
              </div>
            </div>

            {/* Days Remaining */}
            <div className="mb-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-sm">Days Remaining</span>
                <span className={`text-sm font-semibold ${getStatusColor()}`}>
                  {getDaysRemaining()} days
                </span>
              </div>
              
              {/* Progress Bar */}
              <div className="mt-2 bg-gray-700 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-300"
                  style={{ 
                    width: `${Math.max(0, Math.min(100, (getDaysRemaining() / (activePlan?.duration || 1)) * 100))}%` 
                  }}
                />
              </div>
            </div>

            {/* Usage Stats */}
            <div className="mb-4 p-3 bg-gray-700/50 rounded-lg">
              <div className="text-gray-400 text-sm mb-2">Usage This Month</div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">Chat Calls</span>
                  <span className="text-white text-sm font-semibold">
                    {usageStats.chatCalls} / ∞
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">Compatibility Checks</span>
                  <span className="text-white text-sm font-semibold">
                    {usageStats.compatibilityCalls} / ∞
                  </span>
                </div>
              </div>
            </div>

            {/* RRAASI Coins Status */}
            <div className="mb-4 p-3 bg-gradient-to-r from-purple-600/20 to-blue-600/20 border border-purple-500/30 rounded-lg">
              <div className="text-purple-300 text-sm mb-2 flex items-center gap-2">
                <Coins className="w-4 h-4" />
                RRAASI Coins
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">Current Balance</span>
                  <span className="text-white text-sm font-semibold">
                    {balance.total.toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">From Subscription</span>
                  <span className="text-blue-300 text-sm font-semibold">
                    +{balance.subscription.toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">Bonus Coins</span>
                  <span className="text-yellow-300 text-sm font-semibold">
                    +{balance.bonus.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowDetails(false)
                  setShowPlans(true)
                }}
                className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white py-2 rounded-lg text-sm font-semibold hover:from-purple-600 hover:to-pink-600 transition-all"
              >
                Renew
              </button>
              
              <button
                onClick={() => setShowDetails(false)}
                className="flex-1 bg-gray-700 text-white py-2 rounded-lg text-sm font-semibold hover:bg-gray-600 transition-colors"
              >
                <Settings className="w-4 h-4 mx-auto" />
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Subscription Plans Modal */}
      {showPlans && (
        <SubscriptionPlans onClose={() => setShowPlans(false)} />
      )}
    </div>
  )
}

export default SubscriptionStatus 